// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// Unauthorized copying, reverse engineering, or AI-assisted
// reproduction of this software is strictly prohibited.
// Protected under US Copyright Law (17 U.S.C.) and international
// treaties. Patent Pending. License: PROPRIETARY
// ================================================================
// Platform Analytics Dashboard — Founders Only
// Tracks: page views, user sessions, interactions, errors across all apps

import React, { useState, useEffect, useCallback } from 'react';
import { supabase } from '../supabase';
import { useAuth } from '../AuthContext';
import { ADMIN_TEAM } from '../components/Layout';

const styles = {
  page: { padding: '24px', maxWidth: '1400px' },
  header: { marginBottom: '24px' },
  title: { fontFamily: "'Oswald', sans-serif", fontSize: '1.8rem', fontWeight: 700, color: '#E0E0E0', letterSpacing: '2px', margin: 0 },
  subtitle: { color: '#666', fontSize: '0.75rem', marginTop: '4px' },
  tabs: { display: 'flex', gap: '4px', marginBottom: '24px', borderBottom: '1px solid #2A2A2A', paddingBottom: '8px' },
  tab: (active) => ({
    padding: '8px 16px', background: active ? '#4F46E5' : 'transparent', color: active ? '#FFFFFF' : '#888',
    border: 'none', borderRadius: '6px 6px 0 0', cursor: 'pointer', fontFamily: "'Oswald', sans-serif",
    fontSize: '0.75rem', letterSpacing: '1.5px', textTransform: 'uppercase', fontWeight: active ? 700 : 400,
  }),
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '12px', marginBottom: '24px' },
  statCard: { background: '#1E293B', border: '1px solid #334155', borderRadius: '10px', padding: '16px' },
  statLabel: { color: '#94A3B8', fontSize: '0.6rem', letterSpacing: '1.5px', textTransform: 'uppercase', fontFamily: "'Oswald', sans-serif", marginBottom: '6px' },
  statValue: (color) => ({ fontFamily: "'JetBrains Mono', monospace", fontSize: '1.6rem', fontWeight: 700, color: color || '#818CF8', lineHeight: 1 }),
  card: { background: '#1E293B', border: '1px solid #334155', borderRadius: '10px', padding: '20px', marginBottom: '16px' },
  cardTitle: { fontFamily: "'Oswald', sans-serif", fontSize: '0.8rem', color: '#94A3B8', letterSpacing: '1.5px', textTransform: 'uppercase', marginBottom: '16px' },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { color: '#64748B', fontSize: '0.6rem', letterSpacing: '1.5px', textTransform: 'uppercase', fontFamily: "'Oswald', sans-serif", padding: '8px 12px', textAlign: 'left', borderBottom: '1px solid #334155' },
  td: { color: '#CBD5E1', fontSize: '0.75rem', fontFamily: "'JetBrains Mono', monospace", padding: '8px 12px', borderBottom: '1px solid #1E293B' },
  tdHighlight: { color: '#818CF8', fontSize: '0.75rem', fontFamily: "'JetBrains Mono', monospace", padding: '8px 12px', borderBottom: '1px solid #1E293B' },
  empty: { color: '#64748B', fontSize: '0.8rem', textAlign: 'center', padding: '40px', fontStyle: 'italic' },
  refreshBtn: {
    padding: '8px 16px', background: 'transparent', color: '#818CF8', border: '1px solid #334155',
    borderRadius: '6px', cursor: 'pointer', fontFamily: "'JetBrains Mono', monospace", fontSize: '0.7rem',
  },
  barRow: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '8px' },
  barLabel: { color: '#94A3B8', fontSize: '0.6rem', fontFamily: "'JetBrains Mono', monospace", minWidth: '120px', textAlign: 'right', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' },
  barTrack: { flex: 1, background: '#0F172A', borderRadius: '3px', height: '14px', overflow: 'hidden' },
  barFill: (pct, color) => ({ width: `${Math.min(pct, 100)}%`, height: '100%', background: color || '#818CF8', borderRadius: '3px', transition: 'width 0.5s' }),
  barValue: { color: '#CBD5E1', fontSize: '0.65rem', fontFamily: "'JetBrains Mono', monospace", minWidth: '40px' },
  select: {
    padding: '6px 10px', background: '#0F172A', border: '1px solid #334155', borderRadius: '6px',
    color: '#CBD5E1', fontSize: '0.75rem', fontFamily: "'JetBrains Mono', monospace",
  },
};

function timeAgo(dateStr) {
  if (!dateStr) return 'never';
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

function formatDate(dateStr) {
  if (!dateStr) return '-';
  return new Date(dateStr).toLocaleString('en-US', {
    month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true,
  });
}

export default function PlatformAnalyticsPage() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('overview');
  const [timeRange, setTimeRange] = useState('24h');
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalPageViews: 0, uniqueUsers: 0, activeSessions: 0, totalErrors: 0,
    topPages: [], recentUsers: [], recentErrors: [], userJourneys: [],
    interactions: [],
  });

  // Check founder access — only ADMIN_TEAM members
  const isFounder = user?.email && ADMIN_TEAM?.some(e => e.toLowerCase() === user.email.toLowerCase());

  const getTimeFilter = useCallback(() => {
    const now = new Date();
    switch (timeRange) {
      case '1h': return new Date(now - 3600000).toISOString();
      case '24h': return new Date(now - 86400000).toISOString();
      case '7d': return new Date(now - 604800000).toISOString();
      case '30d': return new Date(now - 2592000000).toISOString();
      case 'all': return '2020-01-01T00:00:00Z';
      default: return new Date(now - 86400000).toISOString();
    }
  }, [timeRange]);

  const loadData = useCallback(async () => {
    setLoading(true);
    const since = getTimeFilter();

    try {
      const [pvRes, uiRes, sessRes, errRes, topRes, journeyRes] = await Promise.all([
        supabase.from('page_views').select('id', { count: 'exact', head: true }).gte('timestamp', since),
        supabase.from('user_interactions').select('*').gte('timestamp', since).order('timestamp', { ascending: false }).limit(50),
        supabase.from('user_sessions').select('*').order('started_at', { ascending: false }).limit(50),
        supabase.from('error_logs').select('*').gte('timestamp', since).order('timestamp', { ascending: false }).limit(50),
        supabase.from('page_views').select('route, email, user_id').gte('timestamp', since),
        supabase.from('page_views').select('email, route, page_title, timestamp').gte('timestamp', since).order('timestamp', { ascending: false }).limit(200),
      ]);

      const pageCounts = {};
      const uniqueEmails = new Set();
      (topRes.data || []).forEach(pv => {
        pageCounts[pv.route] = (pageCounts[pv.route] || 0) + 1;
        if (pv.email) uniqueEmails.add(pv.email);
      });
      const topPages = Object.entries(pageCounts)
        .map(([route, count]) => ({ route, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, 15);

      const userMap = {};
      (journeyRes.data || []).forEach(pv => {
        if (!pv.email) return;
        if (!userMap[pv.email]) {
          userMap[pv.email] = { email: pv.email, lastSeen: pv.timestamp, pages: 1, lastPage: pv.route };
        } else {
          userMap[pv.email].pages++;
        }
      });
      const recentUsers = Object.values(userMap).sort((a, b) => new Date(b.lastSeen) - new Date(a.lastSeen));

      setStats({
        totalPageViews: pvRes.count || 0,
        uniqueUsers: uniqueEmails.size,
        activeSessions: (sessRes.data || []).length,
        totalErrors: (errRes.data || []).length,
        topPages,
        recentUsers,
        recentErrors: errRes.data || [],
        userJourneys: journeyRes.data || [],
        interactions: uiRes.data || [],
        sessions: sessRes.data || [],
      });
    } catch (err) {
      console.error('Analytics load error:', err);
    }

    setLoading(false);
  }, [getTimeFilter]);

  useEffect(() => { loadData(); }, [loadData]);

  if (!isFounder) {
    return (
      <div style={styles.page}>
        <div style={{ ...styles.card, textAlign: 'center' }}>
          <div style={{ color: '#EF4444', fontSize: '1.2rem', fontFamily: "'Oswald', sans-serif", marginBottom: '8px' }}>
            ACCESS DENIED
          </div>
          <div style={{ color: '#64748B', fontSize: '0.8rem' }}>
            Platform analytics is restricted to founders only.
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      <div style={{ ...styles.header, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h1 style={styles.title}>PLATFORM ANALYTICS</h1>
          <div style={styles.subtitle}>User activity, sessions, and system health across all apps</div>
        </div>
        <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
          <select style={styles.select} value={timeRange} onChange={(e) => setTimeRange(e.target.value)}>
            <option value="1h">Last Hour</option>
            <option value="24h">Last 24h</option>
            <option value="7d">Last 7 Days</option>
            <option value="30d">Last 30 Days</option>
            <option value="all">All Time</option>
          </select>
          <button style={styles.refreshBtn} onClick={loadData} disabled={loading}>
            {loading ? 'LOADING...' : 'REFRESH'}
          </button>
        </div>
      </div>

      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Page Views</div>
          <div style={styles.statValue('#818CF8')}>{stats.totalPageViews.toLocaleString()}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Unique Users</div>
          <div style={styles.statValue('#4ECDC4')}>{stats.uniqueUsers}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Sessions</div>
          <div style={styles.statValue('#A78BFA')}>{stats.activeSessions}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Errors</div>
          <div style={styles.statValue(stats.totalErrors > 0 ? '#EF4444' : '#4ECDC4')}>{stats.totalErrors}</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statLabel}>Interactions</div>
          <div style={styles.statValue('#FBBF24')}>{stats.interactions?.length || 0}</div>
        </div>
      </div>

      <div style={styles.tabs}>
        {['overview', 'users', 'pages', 'sessions', 'errors'].map(tab => (
          <button key={tab} style={styles.tab(activeTab === tab)} onClick={() => setActiveTab(tab)}>
            {tab}
          </button>
        ))}
      </div>

      {activeTab === 'overview' && (
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
          <div style={styles.card}>
            <div style={styles.cardTitle}>Top Pages</div>
            {stats.topPages.length === 0 ? (
              <div style={styles.empty}>No page views yet. Data will appear as users browse.</div>
            ) : (
              stats.topPages.map((p, i) => {
                const max = stats.topPages[0]?.count || 1;
                return (
                  <div key={i} style={styles.barRow}>
                    <div style={styles.barLabel}>{p.route}</div>
                    <div style={styles.barTrack}>
                      <div style={styles.barFill((p.count / max) * 100)} />
                    </div>
                    <div style={styles.barValue}>{p.count}</div>
                  </div>
                );
              })
            )}
          </div>

          <div style={styles.card}>
            <div style={styles.cardTitle}>Recent Active Users</div>
            {stats.recentUsers.length === 0 ? (
              <div style={styles.empty}>No user activity yet.</div>
            ) : (
              <table style={styles.table}>
                <thead><tr><th style={styles.th}>User</th><th style={styles.th}>Last Seen</th><th style={styles.th}>Pages</th><th style={styles.th}>Last Page</th></tr></thead>
                <tbody>
                  {stats.recentUsers.slice(0, 15).map((u, i) => (
                    <tr key={i}>
                      <td style={styles.tdHighlight}>{u.email?.split('@')[0]}</td>
                      <td style={styles.td}>{timeAgo(u.lastSeen)}</td>
                      <td style={styles.td}>{u.pages}</td>
                      <td style={styles.td}>{u.lastPage}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div style={{ ...styles.card, gridColumn: '1 / -1' }}>
            <div style={styles.cardTitle}>Recent Errors</div>
            {stats.recentErrors.length === 0 ? (
              <div style={styles.empty}>No errors — system is clean.</div>
            ) : (
              <table style={styles.table}>
                <thead><tr><th style={styles.th}>Time</th><th style={styles.th}>Type</th><th style={styles.th}>Message</th><th style={styles.th}>Route</th><th style={styles.th}>User</th></tr></thead>
                <tbody>
                  {stats.recentErrors.slice(0, 10).map((e, i) => (
                    <tr key={i}>
                      <td style={styles.td}>{timeAgo(e.timestamp)}</td>
                      <td style={{ ...styles.td, color: '#EF4444' }}>{e.error_type}</td>
                      <td style={{ ...styles.td, maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.message}</td>
                      <td style={styles.td}>{e.route}</td>
                      <td style={styles.td}>{e.email?.split('@')[0] || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {activeTab === 'users' && (
        <div style={styles.card}>
          <div style={styles.cardTitle}>All User Activity</div>
          {stats.recentUsers.length === 0 ? (
            <div style={styles.empty}>No user activity recorded yet.</div>
          ) : (
            <table style={styles.table}>
              <thead><tr><th style={styles.th}>#</th><th style={styles.th}>Email</th><th style={styles.th}>Last Active</th><th style={styles.th}>Total Pages</th><th style={styles.th}>Last Page</th></tr></thead>
              <tbody>
                {stats.recentUsers.map((u, i) => (
                  <tr key={i}>
                    <td style={styles.td}>{i + 1}</td>
                    <td style={styles.tdHighlight}>{u.email}</td>
                    <td style={styles.td}>{formatDate(u.lastSeen)}</td>
                    <td style={styles.td}>{u.pages}</td>
                    <td style={styles.td}>{u.lastPage}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'pages' && (
        <div style={styles.card}>
          <div style={styles.cardTitle}>Page View Breakdown</div>
          {stats.topPages.length === 0 ? (
            <div style={styles.empty}>No page views recorded yet.</div>
          ) : (
            <table style={styles.table}>
              <thead><tr><th style={styles.th}>#</th><th style={styles.th}>Route</th><th style={styles.th}>Views</th><th style={styles.th}>Bar</th></tr></thead>
              <tbody>
                {stats.topPages.map((p, i) => {
                  const max = stats.topPages[0]?.count || 1;
                  return (
                    <tr key={i}>
                      <td style={styles.td}>{i + 1}</td>
                      <td style={styles.tdHighlight}>{p.route}</td>
                      <td style={styles.td}>{p.count}</td>
                      <td style={{ ...styles.td, width: '40%' }}>
                        <div style={styles.barTrack}><div style={styles.barFill((p.count / max) * 100)} /></div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'sessions' && (
        <div style={styles.card}>
          <div style={styles.cardTitle}>Recent Sessions</div>
          {(stats.sessions || []).length === 0 ? (
            <div style={styles.empty}>No sessions recorded yet.</div>
          ) : (
            <table style={styles.table}>
              <thead><tr><th style={styles.th}>User</th><th style={styles.th}>Started</th><th style={styles.th}>Device</th><th style={styles.th}>Browser</th><th style={styles.th}>OS</th><th style={styles.th}>App</th></tr></thead>
              <tbody>
                {(stats.sessions || []).map((s, i) => (
                  <tr key={i}>
                    <td style={styles.tdHighlight}>{s.email?.split('@')[0] || '-'}</td>
                    <td style={styles.td}>{formatDate(s.started_at)}</td>
                    <td style={styles.td}>{s.device_type || '-'}</td>
                    <td style={styles.td}>{s.browser || '-'}</td>
                    <td style={styles.td}>{s.os || '-'}</td>
                    <td style={styles.td}>{s.app_name || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {activeTab === 'errors' && (
        <div style={styles.card}>
          <div style={styles.cardTitle}>Error Log</div>
          {stats.recentErrors.length === 0 ? (
            <div style={styles.empty}>No errors logged. System is healthy.</div>
          ) : (
            <table style={styles.table}>
              <thead><tr><th style={styles.th}>Time</th><th style={styles.th}>Type</th><th style={styles.th}>Message</th><th style={styles.th}>Route</th><th style={styles.th}>User</th><th style={styles.th}>App</th></tr></thead>
              <tbody>
                {stats.recentErrors.map((e, i) => (
                  <tr key={i}>
                    <td style={styles.td}>{formatDate(e.timestamp)}</td>
                    <td style={{ ...styles.td, color: '#EF4444' }}>{e.error_type}</td>
                    <td style={{ ...styles.td, maxWidth: '300px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{e.message}</td>
                    <td style={styles.td}>{e.route || '-'}</td>
                    <td style={styles.td}>{e.email?.split('@')[0] || '-'}</td>
                    <td style={styles.td}>{e.app_name || '-'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}
    </div>
  );
}
